import React, { cloneElement, isValidElement } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    pageVariants,
    dashboardVariants,
} from '../../design-system/motion';
import InstitutionContextBanner from './InstitutionContextBanner';
import './AppLayout.css';

interface AppLayoutProps {
    children: React.ReactNode;
}

/**
 * AppLayout provides CSS Grid shell + page-transition animations.
 *
 * Grid areas:
 *   sidebar | topbar
 *   sidebar | content
 *
 * The sidebar is position:fixed, so the grid column is a spacer that
 * pushes content right. TopBar is rendered separately in App.tsx.
 */
export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const location = useLocation();
    const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';

    const routesWithLocation = isValidElement(children)
        ? cloneElement(children as React.ReactElement<any>, { location })
        : children;

    return (
        <div className="app-layout">
            <main className="app-layout__content">
                <InstitutionContextBanner />
                <AnimatePresence mode="wait">
                    <motion.div
                        key={location.pathname}
                        variants={isDashboard ? dashboardVariants : pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            minHeight: '100%',
                            transformOrigin: 'center center',
                        }}
                    >
                        {routesWithLocation}
                    </motion.div>
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AppLayout;
